import React, { useState } from 'react';
import { useLocation, Link as RouterLink } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Mail } from 'lucide-react';
import { useConfirmSignup, useResendConfirmation } from '../hooks/useAuth';
import { ROUTES } from '../../../config/routes';
import { Input } from '../../../components/ui/input';
import { Button } from '../../../components/ui/button';
import { Alert } from '../../../components/ui/alert';

const confirmSchema = z.object({
  code: z
    .string()
    .min(6, '確認コードは6桁です')
    .max(6, '確認コードは6桁です')
    .regex(/^\d+$/, '数字のみ入力してください'),
});

type ConfirmFormData = z.infer<typeof confirmSchema>;

export const ConfirmEmailForm: React.FC = () => {
  const location = useLocation();
  const email = location.state?.email || '';
  const [resendCooldown, setResendCooldown] = useState(0);

  const { mutate: confirm, isPending } = useConfirmSignup();
  const { mutate: resend, isPending: isResending } = useResendConfirmation();

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<ConfirmFormData>({
    resolver: zodResolver(confirmSchema),
  });

  const onSubmit = (data: ConfirmFormData) => {
    confirm({ email, code: data.code });
  };

  const handleResend = () => {
    if (resendCooldown > 0) return;
    resend(email, {
      onSuccess: () => {
        setResendCooldown(60);
        const interval = setInterval(() => {
          setResendCooldown((prev) => {
            if (prev <= 1) {
              clearInterval(interval);
              return 0;
            }
            return prev - 1;
          });
        }, 1000);
      },
    });
  };

  if (!email) {
    return (
      <div className="w-full">
        <Alert variant="error" className="mb-6">
          メールアドレス情報がありません。新規登録からやり直してください。
        </Alert>
        <RouterLink to={ROUTES.SIGNUP}>
          <Button className="w-full h-10">新規登録へ</Button>
        </RouterLink>
      </div>
    );
  }

  return (
    <div className="w-full text-center">
      {/* メールアイコン (teal circle) */}
      <div
        style={{
          width: 52,
          height: 52,
          borderRadius: '50%',
          background: '#e6f4f3',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          margin: '0 auto 16px',
        }}
      >
        <Mail size={24} style={{ color: '#0a7a72' }} />
      </div>

      <h1 className="text-xl font-bold text-text-primary mb-2">
        メールアドレスの確認
      </h1>
      <p className="text-sm text-gray-500 mb-6 leading-relaxed">
        {email} に送信した<br />
        6桁の確認コードを入力してください
      </p>

      <form onSubmit={handleSubmit(onSubmit)} noValidate className="w-full">
        <div className="mb-5">
          <Input
            {...register('code')}
            placeholder="123456"
            style={{ textAlign: 'center', letterSpacing: '0.5em', fontSize: 22 }}
            maxLength={6}
            inputMode="numeric"
            className="!text-[22px]"
            aria-invalid={!!errors.code}
            aria-describedby="confirm-code-error"
          />
          {errors.code && (
            <p id="confirm-code-error" className="text-sm text-red-500 mt-1">
              {errors.code.message}
            </p>
          )}
        </div>

        <Button
          type="submit"
          className="w-full !h-auto !py-3.5 !text-base !font-bold !rounded-lg mb-4 md:!py-3 md:!text-[15px]"
          disabled={isPending}
        >
          {isPending ? '確認中...' : '確認して続ける'}
        </Button>

        <p className="text-sm text-gray-500 mt-2">
          コードが届かない場合は{' '}
          <button
            type="button"
            onClick={handleResend}
            disabled={isResending || resendCooldown > 0}
            className="text-primary hover:underline font-semibold disabled:opacity-50 disabled:no-underline"
          >
            {resendCooldown > 0 ? `${resendCooldown}秒後に再送信` : '再送信'}
          </button>
        </p>
      </form>
    </div>
  );
};
